/**
 * Analyzed News API – news articles that were used in risk analysis for a company.
 * Data from Firebase only (company + risk analyses + news). No external News API.
 * @see API doc: GET /api/v1/analyzed-news/company/{ticker}
 * @see API doc: POST /api/v1/analyzed-news/batch
 */
import { API_BASE_URL as API_BASE } from '../config/api';
import { createNewsArticle } from '../models/companyModels';
import { createAuthenticatedApiClient } from './authenticatedApiClient';
import { withRequestCache } from './requestCache';

const apiClient = createAuthenticatedApiClient({
  baseURL: `${API_BASE}/api/v1`,
});

const TICKER_BATCH_SIZE = 50;

/** Valid sentiment values from risk analysis; used for badges and filtering. */
const SENTIMENT_VALUES = ['positive', 'negative', 'neutral'];

const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const batchCacheKey = (tickers) =>
  `analyzed-news:batch:${tickers.slice().sort().join(',')}`;

/**
 * Map analyzed-news API article to NewsArticleDTO shape (article_id, published_at, relevance_score, sentiment, etc.)
 * API provides sentiment from risk analysis: "positive" | "negative" | "neutral".
 */
function mapToNewsArticleDTO(article) {
  const relevance = article.relevanceScore != null ? Number(article.relevanceScore) : null;
  const rawSentiment = (article.sentiment && typeof article.sentiment === 'string')
    ? article.sentiment.toLowerCase().trim() : null;
  const sentiment = SENTIMENT_VALUES.includes(rawSentiment) ? rawSentiment : 'neutral';
  return createNewsArticle({
    article_id: article.id,
    title: article.title,
    description: article.description ?? null,
    content: article.content ?? null,
    url: article.url ?? null,
    published_at: article.publishedAt ?? null,
    source: article.source ?? '',
    sentiment,
    relevance_score: relevance != null && relevance <= 1 ? Math.round(relevance * 100) : relevance,
    risk_matches: article.riskMatches ?? [],
    keywords: article.riskCategories ?? [],
  });
}

const mapArticlesByTicker = (rawData, normalizedTickers) => {
  const mapped = {};
  normalizedTickers.forEach((ticker) => {
    const list = Array.isArray(rawData[ticker]) ? rawData[ticker] : [];
    mapped[ticker] = list.map(mapToNewsArticleDTO);
  });
  return mapped;
};

/**
 * Get news articles that were used in the latest and past 5 risk analyses for the company.
 * @param {string} ticker - Company ticker, e.g. AAPL, MSFT
 * @returns {Promise<{ success: boolean, data: NewsArticleDTO[], message?: string }>}
 */
export async function getAnalyzedNewsForCompany(ticker) {
  try {
    const response = await withRequestCache({
      key: `analyzed-news:company:${ticker}`,
      ttlMs: 2 * 60 * 1000,
      requestFn: () => apiClient.get(`analyzed-news/company/${encodeURIComponent(ticker)}`),
    });
    const success = response.data?.success !== false;
    const rawList = response.data?.data;
    const list = Array.isArray(rawList) ? rawList.map(mapToNewsArticleDTO) : [];
    return {
      success: success && Array.isArray(rawList),
      data: list,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || error.message || 'Failed to fetch analyzed news',
    };
  }
}

export async function getAnalyzedNewsBatch(tickers = []) {
  const normalizedTickers = Array.from(
    new Set(
      (Array.isArray(tickers) ? tickers : [])
        .map((ticker) => String(ticker || '').trim().toUpperCase())
        .filter(Boolean)
    )
  );
  if (normalizedTickers.length === 0) {
    return { success: true, data: {} };
  }

  const merged = {};
  const chunks = chunkArray(normalizedTickers, TICKER_BATCH_SIZE);

  for (const chunk of chunks) {
    try {
      const response = await withRequestCache({
        key: batchCacheKey(chunk),
        ttlMs: 2 * 60 * 1000,
        requestFn: () => apiClient.post('analyzed-news/batch', { tickers: chunk }),
      });
      const success = response.data?.success !== false;
      const rawData = response.data?.data?.articlesByTicker ?? response.data?.data ?? {};
      Object.assign(merged, mapArticlesByTicker(rawData, chunk));
      if (!success) {
        return {
          success: false,
          data: merged,
          message: response.data?.message,
        };
      }
    } catch (batchError) {
      if (batchError.response?.status === 404) {
        const fallbackResults = await Promise.all(
          chunk.map(async (ticker) => {
            const result = await getAnalyzedNewsForCompany(ticker);
            return [ticker, result.success ? result.data : []];
          })
        );
        Object.assign(merged, Object.fromEntries(fallbackResults));
      } else {
        return {
          success: false,
          data: merged,
          message: batchError.response?.data?.message || batchError.message || 'Failed to fetch analyzed news batch',
        };
      }
    }
  }

  return { success: true, data: merged };
}

export async function getAnalyzedNewsForTickers(tickers = []) {
  return getAnalyzedNewsBatch(tickers);
}

export const analyzedNewsService = {
  getAnalyzedNewsForCompany,
  getAnalyzedNewsForTickers,
  getAnalyzedNewsBatch,
};
